'use client';

import type { ChangeEvent } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Badge,
  Button,
  LegacyDialog,
  Field,
  Text,
  Textarea,
  Tooltip,
} from '@terra/ui-design-system';
import type { MemoryRecordResponse } from '@terra/shared-contracts-memory';
import { useDateFormatter } from '../../hooks/useDateFormatter';

type EndpointMemoriesDialogsProps = {
  viewMemory: MemoryRecordResponse | null;
  isViewDialogOpen: boolean;
  onViewDialogOpenChange: (open: boolean) => void;
  editorOpen: boolean;
  editingMemory: MemoryRecordResponse | null;
  editorContent: string;
  isSubmitting: boolean;
  onEditorOpenChange: (open: boolean) => void;
  onEditorContentChange: (content: string) => void;
  onSave: () => void;
};

export function EndpointMemoriesDialogs({
  viewMemory,
  isViewDialogOpen,
  onViewDialogOpenChange,
  editorOpen,
  editingMemory,
  editorContent,
  isSubmitting,
  onEditorOpenChange,
  onEditorContentChange,
  onSave,
}: EndpointMemoriesDialogsProps) {
  const dateFormatter = useDateFormatter();
  const t = useTranslations('memories');
  const tCommon = useTranslations('common');

  return (
    <>
      <LegacyDialog.Root
        open={isViewDialogOpen}
        onOpenChange={({ open }) => {
          onViewDialogOpenChange(open);
        }}
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="2xl">
            <VStack gap="3" p="3">
              <LegacyDialog.Title>
                {t('endpointMemories.columnContent')}
              </LegacyDialog.Title>

              {viewMemory && (
                <VStack gap="2" alignItems="stretch" w="full">
                  <HStack gap="1" flexWrap="wrap">
                    <Tooltip
                      title={t(
                        `creationMethodTooltip.${viewMemory.creationMethod ?? 'manual'}`,
                      )}
                    >
                      <Badge variant="fill" size="sm">
                        {t(
                          `creationMethod.${viewMemory.creationMethod ?? 'manual'}`,
                        )}
                      </Badge>
                    </Tooltip>
                    <Badge variant="outline" size="sm">
                      {t(`status.${viewMemory.status ?? 'active'}`)}
                    </Badge>
                  </HStack>
                  {viewMemory.createdAt && (
                    <HStack gap="2">
                      <Text fontSize="xs" color="fg.muted" minW="60px">
                        {t('endpointMemories.columnSaved')}:
                      </Text>
                      <Text fontSize="xs">
                        {dateFormatter.format(new Date(viewMemory.createdAt))}
                      </Text>
                    </HStack>
                  )}
                  <Box
                    p="3"
                    bg="bg.subtle"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="border.subtle"
                  >
                    <Text fontSize="sm" whiteSpace="pre-wrap">
                      {viewMemory.content}
                    </Text>
                  </Box>
                </VStack>
              )}

              <HStack gap="2" w="full" justifyContent="flex-end">
                <LegacyDialog.CloseTrigger asChild>
                  <Button variant="outline" size="sm">
                    {tCommon('close')}
                  </Button>
                </LegacyDialog.CloseTrigger>
              </HStack>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>

      <LegacyDialog.Root
        open={editorOpen}
        onOpenChange={({ open }) => {
          onEditorOpenChange(open);
        }}
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="xl">
            <VStack gap="3" p="3">
              <VStack gap="1" alignItems="flex-start" w="full">
                <LegacyDialog.Title>
                  {editingMemory
                    ? t('endpointMemories.createDialog.editTitle')
                    : t('endpointMemories.createDialog.title')}
                </LegacyDialog.Title>
                <LegacyDialog.Description>
                  {t('endpointMemories.createDialog.description')}
                </LegacyDialog.Description>
              </VStack>

              <Field.Root w="full">
                <Field.Label fontSize="xs">
                  {t('endpointMemories.createDialog.fieldLabel')}
                </Field.Label>
                <Textarea
                  size="sm"
                  value={editorContent}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                    onEditorContentChange(event.target.value)
                  }
                  placeholder={t(
                    'endpointMemories.createDialog.fieldPlaceholder',
                  )}
                  rows={6}
                  resize="vertical"
                />
                <Field.HelperText fontSize="xs">
                  {t('endpointMemories.createDialog.fieldHelperText')}
                </Field.HelperText>
              </Field.Root>

              <HStack gap="2" w="full" justifyContent="flex-end">
                <LegacyDialog.CloseTrigger asChild>
                  <Button size="sm" variant="outline" disabled={isSubmitting}>
                    {tCommon('cancel')}
                  </Button>
                </LegacyDialog.CloseTrigger>
                <Button
                  size="sm"
                  colorPalette="blue"
                  onClick={onSave}
                  loading={isSubmitting}
                  disabled={!editorContent.trim()}
                >
                  {editingMemory ? tCommon('save') : t('addMemory')}
                </Button>
              </HStack>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </>
  );
}
